import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  IconButton,
  Card,
} from '@mui/material';
import {Add as AddIcon} from '@mui/icons-material';
import {a11yProps, TabPanel} from './UrlaubsantragTabPanel';
import {MeineAntraegeTab} from './meineAntraege';
import {UrlaubsantragForm} from './UrlaubsantragForm';
import {OffeneAntraegeTab} from './offeneAntraege';

import dayjs from 'dayjs';
import {GenehmigteAntraegeTab} from './genehmigteAntraege';
import {useLocation, useNavigate} from 'react-router-dom';
import {Urlaubskalender} from './urlaubskalender';
import Urlaubsuebersicht from './urlaubsuebersicht';
import {UrlaubsantragEditData} from '../../../../Contexts/Urlaubsantrag/UrlaubsantragContext';

interface VacationPeriod {
  start: string;
  end: string;
  status: string;
  id: number;
}

interface UrlaubsantragTableProps {
  loading: boolean;
  meineAntraege: any[];
  pendingAntraege: any[];
  approvedAntraege: any[];
  snackbar: any;
  setSnackbar: (value: any) => void;
  formData: any;
  setFormData: (value: any) => void;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  vacationDaysInfo: any;
  remainingVacationDays: number | null;
  pendingVacationDays: number | null;
  initialRemainingDays: number | null;
  calculateProjectedRemainingDays: () => number;
  calculateProjectedPendingDays: () => number;
  deleteRequest: (id: number) => Promise<void>;
  onEditApproved?: (id: number, data: UrlaubsantragEditData) => Promise<void>;
  processRequest: (id: number, status: 'Genehmigt' | 'Abgelehnt') => Promise<void>;
  submitRequest: () => void;
  resetForm: () => void;
  loadPendingRequests: () => void;
  isApprover: boolean;
  existingVacationDates: VacationPeriod[];
  checkForOverlap: (start: dayjs.Dayjs, end: dayjs.Dayjs) => boolean;
}

// Define tab IDs for URL navigation
const TAB_IDS = ['meineAntraege', 'offeneAntraege', 'genehmigteAntraege', 'urlaubskalender', 'urlaubsuebersicht'];

export const UrlaubsantragTable: React.FC<UrlaubsantragTableProps> = ({
  loading,
  meineAntraege,
  pendingAntraege,
  approvedAntraege,
  snackbar,
  setSnackbar,
  formData,
  setFormData,
  openDialog,
  setOpenDialog,
  vacationDaysInfo,
  remainingVacationDays,
  pendingVacationDays,
  initialRemainingDays,
  calculateProjectedRemainingDays,
  calculateProjectedPendingDays,
  deleteRequest,
  onEditApproved,
  processRequest,
  submitRequest,
  resetForm,
  loadPendingRequests,
  isApprover,
  existingVacationDates,
  checkForOverlap,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // Function to get tab index from URL
  const getTabIndexFromURL = () => {
    // Only check for query parameter (?tab=offeneAntraege)
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && TAB_IDS.includes(tabParam)) {
      return TAB_IDS.indexOf(tabParam);
    }

    return 0; // Default to first tab
  };

  // Update tab value based on URL on component mount and URL change
  useEffect(() => {
    const newTabIndex = getTabIndexFromURL();

    // Only change tab if it exists and user has permission
    if (newTabIndex > 0 && !isApprover) {
      // If user tries to access approver tabs but isn't an approver, stay on first tab
      setTabValue(0);
    } else {
      setTabValue(newTabIndex);

      // Load pending requests if switching to that tab
      if (newTabIndex === 1 && isApprover) {
        loadPendingRequests();
      }
    }
  }, [location, isApprover]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);

    // Update URL when tab changes
    if (newValue < TAB_IDS.length) {
      // Using query parameter style
      navigate(`${location.pathname}?tab=${TAB_IDS[newValue]}`, {replace: true});
    }

    if (newValue === 1 && isApprover) {
      loadPendingRequests();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  return (
    <Box sx={{width: '100%'}}>
      <Card sx={{p: 2, mb: 2}}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
            flexDirection: {xs: isMobile ? 'column' : 'row', sm: 'row'},
            gap: {xs: 1, sm: 0},
          }}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{
              fontWeight: 'bold',
              width: isMobile ? '100%' : 'auto',
            }}
            component='h1'>
            Urlaubsantrag
          </Typography>

          {isMobile ? (
            <Button
              variant='contained'
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              size='small'>
              Neuer Antrag
            </Button>
          ) : (
            <Button variant='contained' startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
              Neuer Antrag
            </Button>
          )}
        </Box>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
          }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label='urlaubsantrag tabs'
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              minHeight: isMobile ? 40 : 48,
              '& .MuiTab-root': {
                minHeight: isMobile ? 40 : 48,
                py: isMobile ? 0.5 : 1,
                fontSize: isMobile ? '0.8rem' : 'inherit',
              },
            }}>
            <Tab label='Meine Anträge' {...a11yProps(0)} />
            {isApprover && <Tab label='Offene Anträge' {...a11yProps(1)} />}
            {isApprover && <Tab label='Genehmigte Anträge' {...a11yProps(2)} />}
            {isApprover && <Tab label='Urlaubskalender' {...a11yProps(3)} />}
            {isApprover && <Tab label='Urlaubsübersicht' {...a11yProps(4)} />}
          </Tabs>
        </Box>

        {/* TabPanel with reduced spacing */}
        <Box sx={{py: {xs: 1, sm: 2}}}>
          {/* Meine Anträge Tab */}
          <TabPanel value={tabValue} index={0} reducedPadding={isMobile}>
            <MeineAntraegeTab loading={loading} antraege={meineAntraege} />
          </TabPanel>

          {/* Offene Anträge Tab (für Genehmiger) */}
          {isApprover && (
            <TabPanel value={tabValue} index={1} reducedPadding={isMobile}>
              <OffeneAntraegeTab
                loading={loading}
                antraege={pendingAntraege}
                onProcess={processRequest}
              />
            </TabPanel>
          )}

          {isApprover && (
            <TabPanel value={tabValue} index={2} reducedPadding={isMobile}>
              <GenehmigteAntraegeTab
                loading={loading}
                antraege={approvedAntraege}
                onDelete={deleteRequest}
                onEdit={onEditApproved}
              />
            </TabPanel>
          )}
          {isApprover && (
            <TabPanel value={tabValue} index={3} reducedPadding={isMobile}>
              <Urlaubskalender
                loading={loading}
                antraege={[
                  ...approvedAntraege,
                  ...pendingAntraege.filter(
                    (p) => !approvedAntraege.some((a) => a.id === p.id)
                  ),
                ]}
              />
            </TabPanel>
          )}

          {isApprover && (
            <TabPanel value={tabValue} index={4} reducedPadding={isMobile}>
              <Urlaubsuebersicht />
            </TabPanel>
          )}
        </Box>
      </Card>

      {/* Formular Dialog für neuen Urlaubsantrag */}
      <UrlaubsantragForm
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={submitRequest}
        loading={loading}
        formData={formData}
        setFormData={setFormData}
        vacationDaysInfo={vacationDaysInfo}
        remainingVacationDays={remainingVacationDays}
        pendingVacationDays={pendingVacationDays}
        initialRemainingDays={initialRemainingDays}
        calculateProjectedRemainingDays={calculateProjectedRemainingDays}
        calculateProjectedPendingDays={calculateProjectedPendingDays}
        existingVacationDates={existingVacationDates}
        checkForOverlap={checkForOverlap}
      />

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{width: '100%'}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UrlaubsantragTable;
